'use client';
import { STORAGE_CONFIG } from '@/config/app';
import { logger } from '@/lib/logger';

/**
 * Top Navigation Visibility Context
 *
 * Provides global state management for top navigation icon visibility with localStorage persistence.
 * Controls which icons appear in the top navigation bar based on user preferences.
 *
 * Uses React Context (not just a hook) to ensure immediate reactivity across all components
 * when settings change in the Settings page.
 *
 * @module TopNavVisibilityContext
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

/**
 * Settings interface for top navigation icon visibility
 */
interface TopNavVisibilitySettings {
  showRefresh: boolean;
  showThemeToggle: boolean;
  showNotifications: boolean;
}

/**
 * Context API interface for top navigation visibility management
 */
interface TopNavVisibilityContextType {
  settings: TopNavVisibilitySettings;
  updateSetting: (key: keyof TopNavVisibilitySettings, value: boolean) => void;
  toggleSetting: (key: keyof TopNavVisibilitySettings) => void;
}

const TopNavVisibilityContext = createContext<TopNavVisibilityContextType | undefined>(undefined);

const STORAGE_KEY = STORAGE_CONFIG.keys.topNavVisibility;

const DEFAULT_SETTINGS: TopNavVisibilitySettings = {
  showRefresh: true,
  showThemeToggle: true,
  showNotifications: true,
};

export function TopNavVisibilityProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<TopNavVisibilitySettings>(DEFAULT_SETTINGS);
  const [mounted, setMounted] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    setMounted(true);
    const savedSettings = localStorage.getItem(STORAGE_KEY);

    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      } catch (error) {
        logger.error('Failed to parse top nav visibility settings', 'TopNavVisibility', error);
      }
    }
  }, []);

  // Save settings to localStorage when they change
  useEffect(() => {
    if (mounted) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    }
  }, [settings, mounted]);

  const updateSetting = (key: keyof TopNavVisibilitySettings, value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleSetting = (key: keyof TopNavVisibilitySettings) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const contextValue = useMemo(
    () => ({
      settings,
      updateSetting,
      toggleSetting,
    }),
    [settings]
  );

  // Prevent flash of unstyled content
  if (!mounted) {
    return null;
  }

  return (
    <TopNavVisibilityContext.Provider value={contextValue}>
      {children}
    </TopNavVisibilityContext.Provider>
  );
}

export function useTopNavVisibility() {
  const context = useContext(TopNavVisibilityContext);
  if (context === undefined) {
    throw new Error('useTopNavVisibility must be used within a TopNavVisibilityProvider');
  }
  return context;
}
